import { useEffect, useImperativeHandle, useMemo, useState } from "react";
import PropTypes from "prop-types";
// @mui
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormGroup,
  Grid,
  Paper,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
} from "@mui/material";
// components
import { Controller, useFieldArray, useForm } from "react-hook-form";
import dayjs from "dayjs";
import "dayjs/locale/de";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Icon } from "@iconify/react";
import {
  anredeArray,
  bezahltBisArray,
  defaultSiteFormValues,
  fullMemberFormData,
  gradArray,
} from "../../../components/form/fields/inputFields";
import TextField from "../../../components/form/fields/TextField";
import SelectField from "../../../components/form/fields/SelectField";
import DEDateField from "../../../components/form/fields/DEDateField";
import BeltAvatar from "../../../components/beltavatar/BeltAvatar";
import { getDateFromDateField } from "../../../utils/dataUtils";
import SiteSelectField from "../../../components/form/fields/SiteSelectField";
import DownloadRegistrationPdfButton from "../../../components/download/DownloadRegistrationPdfButton";

// ----------------------------------------------------------------------

export default function EditMemberForm({
  memberData,
  handleEditMember,
  reference,
}) {
  const currentSiteId = String(
    memberData?.standortRef?._id || memberData?.standortRef || ""
  );
  const contractFieldNames = useMemo(
    () => new Set([...fullMemberFormData.slice(4, 11).map((field) => field.value), "payedTill"]),
    [],
  );
  const [showTestings, setShowTestings] = useState(false);
  const [showContractData, setShowContractData] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");
  const [showSubmitErrorToast, setShowSubmitErrorToast] = useState(false);
  const [site, setSite] = useState(currentSiteId);

  const initialTestings = useMemo(
    () => buildTestingRows(memberData?.degree, memberData?.testings),
    [memberData],
  );

  const {
    handleSubmit,
    control,
    watch,
    setError,
    getValues,
    setFocus,
    formState: { errors },
  } = useForm({
    defaultValues: {
      payRegistrationFee: false,
      registrationFee: 0,
      ...memberData,
      testings: initialTestings,
    },
  });

  const {
    fields: testingFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "testings",
  });
  useImperativeHandle(reference, () => ({
    submitForm() {
      handleSubmit(onSubmit, onSubmitError)();
    },
  }));

  const degree = watch("degree");

  useEffect(() => {
    if (degree === undefined || degree === null) return;
    const currentTestings = getValues("testings");
    if (!Array.isArray(currentTestings)) return;
    const nextRows = buildTestingRows(degree, currentTestings);
    replace(nextRows);
  }, [degree, getValues, replace]);

  const onSubmit = (data) => {
    // Check if registration fee should be collected, if true value must be provided!
    if (data.payRegistrationFee && !data.registrationFee) {
      setError("registrationFee", {
        type: "custom",
        message: "Bitte Anmeldegebühr eingeben.",
      });
      handleSubmitError(
        "Speichern nicht möglich: In den Vertragsdaten fehlt die Anmeldegebühr.",
      );
      setShowContractData(true);
      focusField("registrationFee");
      return null;
    }

    setSubmitErrorMessage("");

    const sanitizedTestings = sanitizeTestings(data?.testings);
    const nextSiteId = String(site || "");
    const contractMembersCount = Array.isArray(
      memberData?.contractRef?.hasMembers
    )
      ? memberData.contractRef.hasMembers.length
      : null;
    const hasSiteChanged = Boolean(nextSiteId && nextSiteId !== currentSiteId);

    let syncContractSite = false;
    if (hasSiteChanged && memberData?.contractRef?._id) {
      if (contractMembersCount === 1) {
        syncContractSite = true;
      } else if (contractMembersCount > 1) {
        syncContractSite = window.confirm(
          "Dieser Vertrag enthält mehrere Mitglieder. Soll der Standort des Vertrags ebenfalls geändert werden?\n\nOK = Vertrag und Mitglied ändern\nAbbrechen = nur Mitglied ändern"
        );
      }
    }

    handleEditMember({
      ...data,
      standortRef: nextSiteId,
      syncContractSite,
      birthdate: getDateFromDateField(data?.birthdate),
      contractDate: getDateFromDateField(data?.contractDate),
      beginningDate: getDateFromDateField(data?.beginningDate),
      lastTesting: getDateFromDateField(data?.lastTesting),
      testings: sanitizedTestings,
    });
  };

  const handleSubmitError = (message) => {
    setSubmitErrorMessage(message);
    setShowSubmitErrorToast(true);
  };

  const focusField = (fieldName) => {
    if (!fieldName) return;

    window.setTimeout(() => {
      setFocus(fieldName);
      const element =
        document.querySelector(`input[name="${fieldName}"]`) ||
        document.querySelector(`textarea[name="${fieldName}"]`) ||
        document.querySelector(`[name="${fieldName}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
    }, 120);
  };

  const onSubmitError = (formErrors) => {
    const { fieldName, message } = getFirstErrorDetails(formErrors);
    const isContractError =
      contractFieldNames.has(fieldName) || contractFieldNames.has(fieldName?.split(".")?.[0]);

    if (isContractError) {
      setShowContractData(true);
    }

    handleSubmitError(
      message ||
        "Speichern nicht möglich. Bitte überprüfe die markierten Pflichtfelder.",
    );
    focusField(fieldName);
  };

  const hasContractErrors = Object.keys(errors || {}).some((fieldName) =>
    contractFieldNames.has(fieldName),
  );

  const renderSiteInputFields = (fields) => {
    return fields.map((field) => {
      if (field.type === "date") {
        return (
          <Grid
            key={field.value + "-field"}
            item
            xs={12}
            md={field.size ? field.size : 4}
          >
            <DEDateField
              control={control}
              name={field.value}
              label={field.label}
              required={field.required}
              errorMsg={field.errorMsg}
              helperText={field.helperText}
            />
          </Grid>
        );
      }

      return (
        <Grid
          key={field.value + "-field"}
          item
          xs={12}
          md={field.size ? field.size : 4}
        >
          <TextField
            control={control}
            name={field.value}
            label={field.label}
            type={field.type}
            variant="outlined"
            required={field.required}
            errorMsg={field.errorMsg}
            helperText={field.helperText}
            fullWidth
            multiline={field?.multiline}
            endAdornment={field?.endAdornment}
          />
        </Grid>
      );
    });
  };

  const handleAddManualTesting = () => {
    append({
      testingDate: null,
      testingLocation: "",
      testingDegree: "",
      isManual: true,
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit, onSubmitError)} className="form">
        <Grid container spacing={2} alignItems="center">
          {submitErrorMessage && (
            <Grid item xs={12}>
              <Alert
                severity="error"
                onClose={() => setSubmitErrorMessage("")}
                sx={{ borderRadius: 2 }}
              >
                {submitErrorMessage}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <SiteSelectField site={site} setSite={setSite} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6">Mitgliedsdaten</Typography>
          </Grid>
          <Grid item xs={12}>
            <SelectField
              style={{ minWidth: 180 }}
              control={control}
              name="title"
              value="title"
              label="Anrede"
              variant="outlined"
              required={true}
              itemArray={anredeArray}
            />
          </Grid>
          {/* {renderSiteInputFields(fullMemberFormData)} */}
          {renderSiteInputFields(fullMemberFormData.slice(1, 4))}
          <Grid item xs={12} md={4}>
            <TextField
              control={control}
              name="bodyHeightCm"
              label="Körpergröße (ca. in cm)"
              type="number"
              variant="outlined"
              helperText="Wichtig für die passende Ausgabe des Anzugs."
              rules={{
                min: {
                  value: 80,
                  message: "Bitte einen realistischen Wert ab 80 cm eingeben.",
                },
                max: {
                  value: 250,
                  message: "Bitte einen realistischen Wert bis 250 cm eingeben.",
                },
              }}
              endAdornment="cm"
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <Accordion
              expanded={showContractData}
              onChange={() => setShowContractData((prev) => !prev)}
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: hasContractErrors ? "error.main" : "divider",
                background:
                  "linear-gradient(135deg, rgba(33, 150, 243, 0.08), rgba(255, 255, 255, 0.9))",
                boxShadow: hasContractErrors
                  ? "0 0 0 1px rgba(211, 47, 47, 0.2)"
                  : "none",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<Icon icon="mdi:chevron-down" />}
                sx={{ px: 2, py: 1.5 }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(33, 150, 243, 0.12)",
                      color: "primary.main",
                    }}
                  >
                    <Icon icon="mdi:file-document-outline" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1">Vertragsdaten</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Laufzeit, Beitrag, Gebühren und Vertragsdokument
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {/* Registration PDF Download Link */}
                {memberData?.contractRef?.publicRegistrationMeta?.createdViaPublic &&
                 memberData?.contractRef?.registrationConfirmationPdf?.relativePath && (
                  <Box sx={{ mb: 2 }}>
                    <DownloadRegistrationPdfButton
                      contractId={memberData.contractRef._id}
                      contract={memberData.contractRef}
                      variant="chip"
                      size="medium"
                    />
                  </Box>
                )}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "rgba(255, 255, 255, 0.75)",
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    {renderSiteInputFields(fullMemberFormData.slice(4, 10))}
                    <Grid item xs={12} md={4}>
                      <SelectField
                        style={{ minWidth: 180 }}
                        control={control}
                        name="payedTill"
                        value="payedTill"
                        label="Bezahlt bis"
                        variant="outlined"
                        required
                        itemArray={bezahltBisArray}
                        helperText="Bis welchem Monat wurde der Beitrag bereits gezahlt?"
                      />
                    </Grid>
                    {renderSiteInputFields(fullMemberFormData.slice(10, 11))}
                    <Grid item xs={12} md={4}>
                      <FormGroup row>
                        <Controller
                          name="payRegistrationFee"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              onChange={(e) => field.onChange(e.target.checked)}
                              checked={field.value}
                            />
                          )}
                        />
                        <label
                          style={{
                            color: "black",
                            marginLeft: 0,
                            marginTop: "8px",
                            fontWeight: 500,
                            fontSize: "1.1rem",
                          }}
                        >
                          Anmeldegebühr einmalig einziehen?
                        </label>
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Paper>
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6">Zusatz</Typography>
          </Grid>
          <Grid item xs={12} md={2}>
            <SelectField
              style={{ minWidth: 180 }}
              control={control}
              name="degree"
              value="degree"
              label="Gürtelgrad"
              variant="outlined"
              required
              itemArray={gradArray}
            />
          </Grid>
          <Grid alignContent={"center"} item xs={12} md={2}>
            <BeltAvatar beltNumber={degree} size="medium" />
          </Grid>
          {renderSiteInputFields(fullMemberFormData.slice(12, 13))}
          {renderSiteInputFields(fullMemberFormData.slice(13, 16))}
          <Grid item xs={12}>
            <Accordion
              expanded={showTestings}
              onChange={() => setShowTestings((prev) => !prev)}
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                background:
                  "linear-gradient(135deg, rgba(2, 136, 209, 0.08), rgba(255, 255, 255, 0.9))",
                boxShadow: "none",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<Icon icon="mdi:chevron-down" />}
                sx={{ px: 2, py: 1.5 }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(2, 136, 209, 0.12)",
                      color: "primary.main",
                    }}
                  >
                    <Icon icon="mdi:school" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1">Prüfungen</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Prüfdaten bis zum aktuellen Gürtelgrad verwalten
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "rgba(255, 255, 255, 0.75)",
                  }}
                >
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Trage die Prüfungsdaten bis zum aktuellen Gürtelgrad
                        ein. Zusätzlich kannst du weitere Prüfungen manuell
                        erfassen.
                      </Typography>
                    </Box>
                    <Divider />
                    <Grid container spacing={2}>
                      {testingFields.map((field, index) => {
                        const isManual = Boolean(field?.isManual);
                        const degreeLabel = getDegreeLabel(
                          field?.testingDegree,
                        );
                        return (
                          <Grid item xs={12} key={field.id}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: isManual
                                  ? "rgba(2, 136, 209, 0.05)"
                                  : "transparent",
                              }}
                            >
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={3}>
                                  {isManual ? (
                                    <SelectField
                                      style={{ minWidth: 180 }}
                                      control={control}
                                      name={`testings.${index}.testingDegree`}
                                      value={`testings.${index}.testingDegree`}
                                      label="Gürtelgrad"
                                      variant="outlined"
                                      itemArray={gradArray}
                                    />
                                  ) : (
                                    <>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Gürtelgrad
                                      </Typography>
                                      <Typography variant="subtitle1">
                                        {degreeLabel}
                                      </Typography>
                                      <Controller
                                        name={`testings.${index}.testingDegree`}
                                        control={control}
                                        render={({
                                          field: controllerField,
                                        }) => (
                                          <input
                                            type="hidden"
                                            {...controllerField}
                                          />
                                        )}
                                      />
                                    </>
                                  )}
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TestingDateField
                                    control={control}
                                    name={`testings.${index}.testingDate`}
                                    label="Prüfungsdatum"
                                  />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    control={control}
                                    name={`testings.${index}.testingLocation`}
                                    label="Ort"
                                    fullWidth
                                  />
                                </Grid>
                                {isManual && (
                                  <Grid item xs={12} md={2}>
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      onClick={() => remove(index)}
                                      fullWidth
                                    >
                                      Entfernen
                                    </Button>
                                  </Grid>
                                )}
                              </Grid>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                    <Box>
                      <Button
                        variant="outlined"
                        onClick={handleAddManualTesting}
                      >
                        Weitere Prüfung hinzufügen
                      </Button>
                    </Box>
                  </Stack>
                </Paper>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </form>
      <Snackbar
        open={showSubmitErrorToast}
        autoHideDuration={5000}
        onClose={() => setShowSubmitErrorToast(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setShowSubmitErrorToast(false)}
          sx={{ width: "100%" }}
        >
          {submitErrorMessage ||
            "Speichern nicht möglich. Bitte Felder mit Fehlern korrigieren."}
        </Alert>
      </Snackbar>
    </>
  );
}

EditMemberForm.propTypes = {
  siteData: PropTypes.object,
  dialogType: PropTypes.string,
};

EditMemberForm.defaultProps = {
  siteData: defaultSiteFormValues, // Hier den Standardwert festlegen
};

function getDegreeLabel(value) {
  const degreeValue = Number(value);
  const match = gradArray.find((item) => item.value === degreeValue);
  return match?.label ?? "Unbekannt";
}

function normalizeTestings(testings) {
  if (!Array.isArray(testings)) return [];
  return testings.map((entry) => ({
    testingDate: entry?.testingDate ?? null,
    testingLocation: entry?.testingLocation ?? "",
    testingDegree: Number.isFinite(Number(entry?.testingDegree))
      ? Number(entry?.testingDegree)
      : entry?.testingDegree ?? "",
    isManual: Boolean(entry?.isManual),
  }));
}

function buildTestingRows(currentDegree, testings) {
  const normalized = normalizeTestings(testings);
  const degreeValue = Number.isFinite(Number(currentDegree))
    ? Number(currentDegree)
    : null;
  if (degreeValue === null || degreeValue < 0) {
    return normalized.map((entry) => ({ ...entry, isManual: true }));
  }

  const baseDegrees = Array.from({ length: degreeValue + 1 }, (_, idx) => idx);
  const usedIndices = new Set();
  const baseRows = baseDegrees.map((degree) => {
    const matchIndex = normalized.findIndex((entry, index) => {
      if (usedIndices.has(index)) return false;
      return Number(entry.testingDegree) === degree;
    });

    if (matchIndex === -1) {
      return {
        testingDate: null,
        testingLocation: "",
        testingDegree: degree,
        isManual: false,
      };
    }

    usedIndices.add(matchIndex);
    return {
      ...normalized[matchIndex],
      testingDegree: degree,
      isManual: false,
    };
  });

  const extraRows = normalized
    .filter((_, index) => !usedIndices.has(index))
    .map((entry) => ({ ...entry, isManual: true }));

  return [...baseRows, ...extraRows];
}

function sanitizeTestings(testings) {
  if (!Array.isArray(testings)) return [];

  return testings
    .map((entry) => {
      const degreeValue = Number(entry?.testingDegree);
      const hasDegree = Number.isFinite(degreeValue);
      const hasDate = Boolean(entry?.testingDate);
      const hasLocation = Boolean(entry?.testingLocation);
      if (!hasDegree && !hasDate && !hasLocation) {
        return null;
      }

      return {
        testingDate: hasDate ? getDateFromDateField(entry?.testingDate) : null,
        testingLocation: entry?.testingLocation?.trim() || "",
        testingDegree: hasDegree ? degreeValue : null,
      };
    })
    .filter(Boolean);
}

function TestingDateField({ control, name, label }) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
          <DatePicker
            label={label}
            value={field.value ? dayjs(field.value) : null}
            onChange={(newValue) => field.onChange(newValue)}
            slotProps={{
              textField: {
                error: !!fieldState.error,
                helperText: fieldState.error?.message || null,
              },
            }}
          />
        </LocalizationProvider>
      )}
    />
  );
}

function getFirstErrorDetails(errorObject, parentKey = "") {
  if (!errorObject || typeof errorObject !== "object") {
    return { fieldName: "", message: "" };
  }

  const keys = Object.keys(errorObject);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const value = errorObject[key];
    const fieldName = parentKey ? `${parentKey}.${key}` : key;

    if (value?.message) {
      return { fieldName, message: value.message };
    }

    if (value && typeof value === "object") {
      const nested = getFirstErrorDetails(value, fieldName);
      if (nested.fieldName) {
        return nested;
      }
    }
  }

  return { fieldName: "", message: "" };
}
